import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/SossietyDialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import OneSignalService from '@/services/OneSignalService';
import { needsIOSInstall } from '@/utils/platform';
import { Bell } from 'lucide-react';

const MAX_SHOW_COUNT = 3;
const PUSH_SHOW_COUNT_KEY = 'pushNotificationShowCount';
const PUSH_DISMISSED_KEY = 'pushNotificationDismissed';
const PUSH_GRANTED_KEY = 'pushNotificationGranted';
const PUSH_LAST_SHOWN_KEY = 'pushNotificationLastShown';
const MIN_TIME_BETWEEN_SHOWS = 24 * 60 * 60 * 1000;

export const resetPushNotificationState = () => {
  localStorage.removeItem(PUSH_SHOW_COUNT_KEY);
  localStorage.removeItem(PUSH_DISMISSED_KEY);
  localStorage.removeItem(PUSH_GRANTED_KEY);
  localStorage.removeItem(PUSH_LAST_SHOWN_KEY);
};

export const debugPushNotificationState = () => {
  const lastShown = parseInt(localStorage.getItem(PUSH_LAST_SHOWN_KEY) || '0', 10);
  const now = Date.now();
  const timeSinceLastShown = lastShown > 0 ? now - lastShown : 0;
  return {
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'N/A',
    showCount: parseInt(localStorage.getItem(PUSH_SHOW_COUNT_KEY) || '0', 10),
    dismissed: localStorage.getItem(PUSH_DISMISSED_KEY) === '1',
    granted: localStorage.getItem(PUSH_GRANTED_KEY) === '1',
    lastShown: lastShown > 0 ? new Date(lastShown).toISOString() : 'Never',
    hoursSinceLastShown: (timeSinceLastShown / (1000 * 60 * 60)).toFixed(2),
    canShowAgain: lastShown === 0 || timeSinceLastShown >= MIN_TIME_BETWEEN_SHOWS,
  };
};

export const forceShowPushDialog = () => {
  resetPushNotificationState();
  if (typeof window !== 'undefined' && (window as any).__forceShowPushDialogInternal) {
    (window as any).__forceShowPushDialogInternal();
  }
};

export const PushNotificationPermissionDialog: React.FC = () => {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const oneSignalService = OneSignalService;
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [open, setOpen] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showDeniedInstructions, setShowDeniedInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const hasAttemptedShowRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setIsIOS(needsIOSInstall());
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__forceShowPushDialogInternal = () => setOpen(true);
    }
  }, []);

  useEffect(() => {
    const checkState = () => {
      const state = oneSignalService.getSubscriptionState();
      setIsSupported(state.isSupported);
      setIsSubscribed(state.isSubscribed);
    };
    checkState();
    const interval = setInterval(checkState, 1000);
    return () => clearInterval(interval);
  }, [oneSignalService]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (open || hasAttemptedShowRef.current) return;

    let permission: NotificationPermission = 'default';
    if (typeof Notification !== 'undefined' && 'permission' in Notification) {
      permission = Notification.permission;
    }

    if (loading || !user) return;
    if (!isSupported) return;
    if (isSubscribed && permission !== 'default') return;
    if (permission === 'granted') {
      localStorage.setItem(PUSH_GRANTED_KEY, '1');
      return;
    }

    if (permission === 'default') {
      if (localStorage.getItem(PUSH_GRANTED_KEY) === '1') localStorage.removeItem(PUSH_GRANTED_KEY);
      if (localStorage.getItem(PUSH_DISMISSED_KEY) === '1') {
        localStorage.removeItem(PUSH_DISMISSED_KEY);
        localStorage.removeItem(PUSH_SHOW_COUNT_KEY);
        localStorage.removeItem(PUSH_LAST_SHOWN_KEY);
      }
    }

    const dismissed = localStorage.getItem(PUSH_DISMISSED_KEY) === '1';
    const showCount = parseInt(localStorage.getItem(PUSH_SHOW_COUNT_KEY) || '0', 10);
    const lastShown = parseInt(localStorage.getItem(PUSH_LAST_SHOWN_KEY) || '0', 10);
    const now = Date.now();

    if (permission === 'default' || permission === 'denied') {
      if (permission === 'denied' && dismissed && showCount >= MAX_SHOW_COUNT) return;
      if (showCount >= MAX_SHOW_COUNT) {
        localStorage.setItem(PUSH_DISMISSED_KEY, '1');
        return;
      }
      if (lastShown > 0 && now - lastShown < MIN_TIME_BETWEEN_SHOWS) return;

      hasAttemptedShowRef.current = true;
      timerRef.current = setTimeout(() => {
        if (!open) {
          setOpen(true);
          localStorage.setItem(PUSH_SHOW_COUNT_KEY, String(showCount + 1));
          localStorage.setItem(PUSH_LAST_SHOWN_KEY, String(now));
        }
        hasAttemptedShowRef.current = false;
      }, 2000);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        hasAttemptedShowRef.current = false;
      };
    }
  }, [user, loading, isSupported, isSubscribed, open]);

  useEffect(() => {
    if (!open) return;

    const checkPermissionInterval = setInterval(() => {
      if (Notification.permission === 'granted') {
        setOpen(false);
        setShowDeniedInstructions(false);
        oneSignalService.subscribe().catch(() => {});
      }
    }, 1000);

    return () => clearInterval(checkPermissionInterval);
  }, [open, oneSignalService]);

  const handleEnable = async () => {
    setIsSubscribing(true);
    try {
      let currentPermission = Notification.permission;

      if (currentPermission === 'default') {
        currentPermission = await Notification.requestPermission();
      }

      if (currentPermission !== 'granted') {
        if (currentPermission === 'denied') setShowDeniedInstructions(true);
        setIsSubscribing(false);
        return;
      }

      const state = oneSignalService.getSubscriptionState();
      if (!state.isInitialized) {
        const appId = import.meta.env.VITE_ONESIGNAL_APP_ID_DEV || import.meta.env.VITE_ONESIGNAL_APP_ID;
        if (appId) {
          await oneSignalService.initialize(appId);
          await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          setIsSubscribing(false);
          return;
        }
      }

      const success = await Promise.race([
        oneSignalService.subscribe(),
        new Promise<boolean>(resolve => setTimeout(() => resolve(false), 20000)),
      ]);

      if (success) {
        localStorage.setItem(PUSH_GRANTED_KEY, '1');
        localStorage.removeItem(PUSH_SHOW_COUNT_KEY);
        localStorage.removeItem(PUSH_DISMISSED_KEY);
        localStorage.removeItem(PUSH_LAST_SHOWN_KEY);
        hasAttemptedShowRef.current = false;
        setOpen(false);
      }
    } catch {
      if (Notification.permission === 'denied') setShowDeniedInstructions(true);
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleDismiss = () => {
    setOpen(false);
    setShowDeniedInstructions(false);
    hasAttemptedShowRef.current = false;
    const showCount = parseInt(localStorage.getItem(PUSH_SHOW_COUNT_KEY) || '0', 10);
    if (showCount >= MAX_SHOW_COUNT) localStorage.setItem(PUSH_DISMISSED_KEY, '1');
  };

  const handleRefreshAfterEnable = () => {
    localStorage.removeItem(PUSH_DISMISSED_KEY);
    localStorage.removeItem(PUSH_SHOW_COUNT_KEY);
    localStorage.removeItem(PUSH_LAST_SHOWN_KEY);
    window.location.reload();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => { setOpen(newOpen); if (!newOpen) hasAttemptedShowRef.current = false; }}>
      <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl p-0 flex flex-col max-h-[90vh] overflow-hidden">
        <div className="px-6 pt-5 pb-3 flex-shrink-0">
          <SossietyDialogHeader
            title={isIOS ? t('pwa.ios_push_title') : (t('notifications.push_notifications.enable_title') || 'Enable Push Notifications')}
          >
            <SossietyDialogClose onClick={handleDismiss} />
          </SossietyDialogHeader>
        </div>

        <div className="px-6 pb-6 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
          <div className="space-y-4">
            {isIOS ? (
              <>
                <div className="text-center space-y-3">
                  <div className="flex justify-center">
                    <div className="w-14 h-14 bg-sossiety-red/10 rounded-full flex items-center justify-center">
                      <Bell className="w-7 h-7 text-sossiety-red" />
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">
                    {t('pwa.ios_push_description')}
                  </p>
                </div>
                <ol className="space-y-2 text-sm text-gray-700">
                  {[1, 2, 3, 4].map((n) => (
                    <li key={n} className="flex gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sossiety-red/10 text-sossiety-red text-xs font-bold flex items-center justify-center">{n}</span>
                      <span className="break-words">{t(`pwa.ios_push_step_${n}`)}</span>
                    </li>
                  ))}
                </ol>
                <div className="flex gap-3 pt-3 pb-1">
                  <Button onClick={handleDismiss} variant="outline" className="flex-1 text-black border-black hover:bg-gray-100 rounded-full">
                    {t('pwa.not_now')}
                  </Button>
                  <Button onClick={handleDismiss} className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full">
                    {t('pwa.ios_push_confirm')}
                  </Button>
                </div>
              </>
            ) : showDeniedInstructions ? (
              <>
                <div className="text-center space-y-3">
                  <div className="flex justify-center">
                    <div className="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Bell className="w-7 h-7 text-yellow-600" />
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 break-words">
                    {t('notifications.push_notifications.blocked_title') || 'Notifications are Blocked'}
                  </h3>
                  <div className="text-left space-y-2 text-sm text-gray-600">
                    <p className="leading-relaxed break-words">
                      {t('notifications.push_notifications.blocked_description') ||
                        'Push notifications are blocked. You must reset them manually in browser settings:'}
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-2">
                      <ol className="list-decimal list-inside space-y-1 ml-2 text-xs text-yellow-800 break-words">
                        <li>{t('notifications.push_notifications.blocked_step_1') || 'Click the lock icon in the address bar'}</li>
                        <li>{t('notifications.push_notifications.blocked_step_2') || 'Go to "Site settings" or "Page Info"'}</li>
                        <li>{t('notifications.push_notifications.blocked_step_3') || 'Find "Notifications" and change to "Ask" or "Allow"'}</li>
                      </ol>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3 pt-3 pb-1">
                  <Button onClick={handleDismiss} variant="outline" className="flex-1 text-black border-black hover:bg-gray-100 rounded-full">
                    {t('pwa.not_now')}
                  </Button>
                  <Button onClick={handleRefreshAfterEnable} className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full text-sm">
                    {t('notifications.push_notifications.reset_button') || "I've Reset in Settings"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center space-y-3">
                  <div className="flex justify-center">
                    <div className="w-14 h-14 bg-sossiety-red/10 rounded-full flex items-center justify-center">
                      <Bell className="w-7 h-7 text-sossiety-red" />
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 break-words">
                    {t('notifications.push_notifications.enable_title') || 'Stay Updated with Push Notifications'}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed break-words">
                    {t('notifications.push_notifications.enable_description') ||
                      'Get notified when your training ends, receive reminders, and stay connected with your community.'}
                  </p>
                </div>
                <div className="flex gap-3 pt-3 pb-1">
                  <Button onClick={handleDismiss} variant="outline" className="flex-1 text-black border-black hover:bg-gray-100 rounded-full" disabled={isSubscribing}>
                    {t('pwa.not_now')}
                  </Button>
                  <Button onClick={handleEnable} className="flex-1 bg-sossiety-red hover:bg-sossiety-red-hover text-white rounded-full" disabled={isSubscribing}>
                    {isSubscribing
                      ? (t('common.loading') || 'Loading...')
                      : (t('notifications.push_notifications.enable_button') || 'Enable Notifications')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
